import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Route } from 'lucide-react'
import { getRescueFlow } from '../../api/rescue'
import type { RescueEvidence, RescueTask } from '../../api/schemas'
import { QueryBoundary } from '../../components/common/QueryBoundary'
import { StatusBadge } from '../../components/common/Status'
import { TimeAgo } from '../../components/common/TimeAgo'
import { Skeleton } from '../../components/ui/skeleton'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog'
import { hasPermission } from '../../lib/authz'
import { uploadUrl } from '../../lib/format'
import { qk } from '../../lib/query-keys'
import { taskStatusLabel } from '../../lib/task-meta'
import type { TrackTask } from '../../lib/track'
import { useTokenClaims } from '../../stores/auth'
import { cn } from '../../lib/utils'
import { TrackMap } from './TrackMap'

const REFRESH_INTERVAL_MS = 30_000

/**
 * 救援流向面板：任务列表（状态徽标）+ 志愿者轨迹地图 + 证据照片网格。
 * 需 tasks.read 权限，无权限时整体隐藏；数据 30s 轮询。
 */
export function RescueFlowPanel({ eventId, className }: { eventId: string; className?: string }) {
  const claims = useTokenClaims()
  if (!hasPermission(claims, 'tasks.read')) return null
  return <RescueFlowInner eventId={eventId} className={className} />
}

function RescueFlowInner({ eventId, className }: { eventId: string; className?: string }) {
  const query = useQuery({
    queryKey: qk.rescueFlow(eventId),
    queryFn: () => getRescueFlow(eventId),
    enabled: Boolean(eventId),
    refetchInterval: REFRESH_INTERVAL_MS,
    retry: 1,
  })

  return (
    <section className={cn('panel-glass rounded-xl p-5', className)} aria-label="救援流向">
      <div className="mb-4 flex items-center gap-2">
        <Route size={15} className="text-primary" />
        <h2 className="text-sm font-semibold text-foreground">救援流向</h2>
        {query.data && query.data.tasks.length > 0 && (
          <span className="text-[10px] text-muted-foreground">{query.data.tasks.length} 项任务</span>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground/60">每 30 秒自动刷新</span>
      </div>
      <QueryBoundary
        query={query}
        skeleton={
          <div className="space-y-2.5">
            <Skeleton className="h-[180px] rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
          </div>
        }
        isEmpty={(d) => d.tasks.length === 0}
        empty={{
          title: '暂无救援任务',
          description: '事件确认后派发的救援任务与志愿者轨迹将在此展示',
          icon: <Route size={24} />,
        }}
      >
        {(d) => <RescueFlowBody tasks={d.tasks} />}
      </QueryBoundary>
    </section>
  )
}

function RescueFlowBody({ tasks }: { tasks: RescueTask[] }) {
  const [preview, setPreview] = useState<RescueEvidence | null>(null)
  const trackTasks = useMemo<TrackTask[]>(
    () => tasks.filter((t) => t.track.length >= 2),
    [tasks],
  )

  return (
    <>
      {trackTasks.length > 0 ? (
        <TrackMap tasks={trackTasks} />
      ) : (
        <div className="rounded-lg bg-black/20 px-4 py-6 text-center text-[11px] text-muted-foreground">
          志愿者任务开始后显示轨迹
        </div>
      )}

      <div className="mt-4 space-y-2.5">
        {tasks.map((task) => (
          <RescueTaskCard key={task.taskId} task={task} onPreview={setPreview} />
        ))}
      </div>

      <EvidenceDialog evidence={preview} onClose={() => setPreview(null)} />
    </>
  )
}

function RescueTaskCard({ task, onPreview }: { task: RescueTask; onPreview: (evidence: RescueEvidence) => void }) {
  return (
    <div className="rounded-lg bg-black/20 p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] text-foreground/90">{task.taskId}</span>
        <StatusBadge status={task.status} labels={taskStatusLabel} />
        {task.volunteerUserId && (
          <span className="text-[11px] text-muted-foreground">志愿者 {task.volunteerUserId}</span>
        )}
        <span className="ml-auto text-[10px] tabular-nums text-muted-foreground/70">
          轨迹 {task.track.length} 点 · 证据 {task.evidence.length} 份
        </span>
      </div>

      {task.track.length === 0 && (
        <div className="mt-2 text-[11px] text-muted-foreground/70">志愿者任务开始后显示轨迹</div>
      )}

      {task.evidence.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {task.evidence.map((ev) => (
            <button
              key={ev.id}
              type="button"
              onClick={() => onPreview(ev)}
              aria-label={`查看证据 ${ev.note || ev.filePath}`}
              className="overflow-hidden rounded-md border border-border transition hover:border-primary/60"
            >
              <img
                src={uploadUrl(ev.filePath)}
                alt="救援证据缩略图"
                loading="lazy"
                className="size-14 object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function EvidenceDialog({ evidence, onClose }: { evidence: RescueEvidence | null; onClose: () => void }) {
  return (
    <Dialog open={Boolean(evidence)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        {evidence && (
          <>
            <DialogHeader>
              <DialogTitle>救援证据</DialogTitle>
              <DialogDescription>{evidence.note || evidence.filePath}</DialogDescription>
            </DialogHeader>
            <img
              src={uploadUrl(evidence.filePath)}
              alt="救援证据原图"
              className="max-h-[60vh] w-full rounded-lg object-contain"
            />
            <div className="text-right text-[10px] text-muted-foreground">
              采集于 <TimeAgo ts={evidence.createdAt} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * 指挥中心事件抽屉用的紧凑摘要：任务行 + 状态徽标 + 轨迹/证据计数。
 * 无权限、无数据或接口异常时整体隐藏（轨迹渲染交给主地图轨迹图层）。
 */
export function RescueFlowSummary({ eventId }: { eventId: string }) {
  const claims = useTokenClaims()
  const allowed = hasPermission(claims, 'tasks.read')
  const query = useQuery({
    queryKey: qk.rescueFlow(eventId),
    queryFn: () => getRescueFlow(eventId),
    enabled: allowed && Boolean(eventId),
    refetchInterval: REFRESH_INTERVAL_MS,
    retry: 1,
  })
  const tasks = query.data?.tasks ?? []

  if (!allowed || tasks.length === 0) return null

  return (
    <div>
      <div className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        救援流向（{tasks.length}）
      </div>
      <div className="space-y-1.5">
        {tasks.map((task) => (
          <div key={task.taskId} className="flex items-center gap-2 rounded-lg bg-foreground/[.03] px-3 py-2">
            <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-foreground/90">{task.taskId}</span>
            <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
              轨迹 {task.track.length} · 证据 {task.evidence.length}
            </span>
            <StatusBadge status={task.status} labels={taskStatusLabel} />
          </div>
        ))}
      </div>
    </div>
  )
}
